<template>
    <!-- *********************************************
    *                    BASE LAYER                  *
    ********************************************** -->
    <!-- Non-authenticated Area -->
    <router-view
    v-if="nonAuthenticatedArea"
    @backOrCloseClicked="navigateByName(routeList.LOGIN_NAME)"
    @showFeedbackToast="[showFeedbackToast = true, feedbackToastData = $event]"
    @reRenderSvg="emit('reRenderSvg')"
    @updateLoginLoader="emit('updateLoginLoader', $event)"/>
    <!-- Authenticated Area -->
    <div v-else class="authenticated-area">
        <!-- Home & Menu controller for mobile, to handle bottom and other menus -->
        <authenticated-area-menu
        :showLoginSpinner="showLoginSpinner"
        @updateLoginLoader="emit('updateLoginLoader', $event)"
        @applyLoginLoaderExitAnimation="emit('applyLoginLoaderExitAnimation')"/>
        <!-- PopUp Modals Wrapper-->
        <pop-ups-wrapper v-if="showPopupsList && !showLoginSpinner"/>
        <!-- Features with specific routes -->
        <router-view
        v-slot="{ Component }"
        @updateLoginLoader="emit('updateLoginLoader', $event)"
        @applyLoginLoaderExitAnimation="emit('applyLoginLoaderExitAnimation')"
        @backOrCloseClicked="showMobileFlowsWrapper = false">
            <transition name="authenticated-app-flows-animation" mode="out-in" @after-leave="redirectForEmptyRoute">
                <div v-if="showMobileFlowsWrapper" class="authenticated-app-flows-wrapper" :class="flowTypeEntry">
                    <component :is="Component"/>
                </div>
            </transition>
        </router-view>
    </div>
    <!-- Session Expired Modal -->
    <expired-session-modal v-if="getSessionExpiredState"/>
    <!-- Feedback Toasts -->
    <toasts-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import AuthenticatedAreaMenu from '@/components/generic/navigation/mobile/AuthenticatedAreaMenu'
import ExpiredSessionModal from '@/components/generic/modals/centered/ExpiredSession'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import PopUpsWrapper from '@/components/generic/modals/centered/popUps/PopUpsWrapper'

/* Vue &&  Helpers */
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
/* Assets */
import { navigateByPath, navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { saveCurrentDateToLocalStorage } from '@/assets/scripts/windowStorage'
import { validateIfUpEntryRoutes } from '@/router/helpers/validations'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Emmitters */
const emit = defineEmits(['updateLoginLoader', 'applyLoginLoaderExitAnimation', 'reRenderSvg'])

/* Props */
const props = defineProps({
	nonAuthenticatedArea: Boolean,
    showLoginSpinner: Boolean
})

const { updateSessionExpireState } = mapActions(moduleMapper.SESSION)
const { getSessionExpiredState, showPopupsList } = mapGetters(moduleMapper.SESSION)

const redirectForEmptyRoute = () => {
    navigateByPath(routeList.HOME_MOBILE_PATH)
}

const currentRoute = useRoute()
const showMobileFlowsWrapper = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(null)

const validateIfShowMobileFlowsWrapper = () => {
    showMobileFlowsWrapper.value = currentRoute.path !== routeList.HOME_MOBILE_PATH
}

const flowTypeEntry = computed(() => {
    /* If we want a feature to have a entry with a specific animation add it to validations functions */
    return validateIfUpEntryRoutes(currentRoute.fullPath) ? 'up-entry' : 'side-entry'
})

watch(() => currentRoute.fullPath, (newVal, oldVal) => {
    validateIfShowMobileFlowsWrapper()
})

const startBackgroundTimer = () => {
    saveCurrentDateToLocalStorage(performance.now()) // returns a high resolution timestamp in milliseconds
}

const checkCallbackFunction = () => {
    if (props.nonAuthenticatedArea && currentRoute.path !== routeList.INITIAL_ACTIONS_SHOW_SECURE_ASSOCIATION_ENROLLMENT_PATH) return
    console.log('after if')
    const storedDate = window.localStorage.getItem('saved-date')

    const endTime = performance.now() // returns a high resolution timestamp in milliseconds

    const timeDiff = endTime - storedDate // in ms

    const convertToSeconds = Math.floor(timeDiff / 1000) // in sec

    window.localStorage.removeItem('saved-date')
    /* After 30s we will show the Expired Session Modal */
    if (convertToSeconds >= 30) updateSessionExpireState(true)
}

document.addEventListener('pause', startBackgroundTimer, false)
document.addEventListener('resume', checkCallbackFunction, false)

onMounted(() => {
    validateIfShowMobileFlowsWrapper()
})
</script>

<style lang="scss" scoped>
.authenticated-area {
    width: 100%;
    height: 100%;

    display: flex;
    overflow: hidden;
    position: relative;

    background-color: var(--secondary-light-3);

    &:before {
        content: '';
        width: 100%;
        height: 80%;
        position: absolute;

        background-image: var(--gradient-horizontal);
    }

    :deep(.authenticated-app-flows-wrapper) {
        width: 100%;
        height: 100%;

        position: absolute;
        top: 0;
        left: 0;
        z-index: 11;

        background-color: var(--secondary-light-4);
        transition: transform 0.4s linear;

        &.up-entry {
            transform: translateY(0);

            &.authenticated-app-flows-animation-enter-active,
            &.authenticated-app-flows-animation-leave-active {
                transform:translateY(0);
            }

            &.authenticated-app-flows-animation-enter-from,
            &.authenticated-app-flows-animation-leave-to {
                transform:translateY(110%);
            }
        }

        &.side-entry {
            transform: translateX(0);

            &.authenticated-app-flows-animation-enter-active,
            &.authenticated-app-flows-animation-leave-active {
                transform:translateX(0);
            }

            &.authenticated-app-flows-animation-enter-from,
            &.authenticated-app-flows-animation-leave-to {
                transform:translateX(110%);
            }
        }
    }
}
</style>
